import { STORAGE_ERROR_CODES, isApiError, isUploadError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { useCallback } from "react";
import { useErrorMessage } from "./api-errors";

const STORAGE_KEYS: Readonly<Record<string, string>> = {
  [STORAGE_ERROR_CODES.unreachable]: "common.upload.unreachable",
  [STORAGE_ERROR_CODES.rejected]: "common.upload.rejected",
  [STORAGE_ERROR_CODES.failed]: "common.upload.storageError",
  [STORAGE_ERROR_CODES.aborted]: "common.upload.cancelled",
};
const SERVER_FAULT = 500;

/**
 * User-facing line for any upload failure. Storage-side failures use storage copy (`storageCopy`
 * overrides it, e.g. a screen's own wording); presign/finalize failures show the API's own message
 * (plan limits, unsupported type, antivirus...). When the user cannot fix the cause - storage
 * refused the PUT, the browser got no response, or the server faulted - `Reference: stage/status`
 * is appended so support can find the request in the API logs.
 */
export function useUploadErrorMessage(): (error: unknown, storageCopy?: string) => string {
  const { t } = useTranslation();
  const apiMessage = useErrorMessage();
  return useCallback(
    (error: unknown, storageCopy?: string) => {
      if (!isUploadError(error)) return apiMessage(error);
      const base = error.isStorageFailure ? (storageCopy ?? t(STORAGE_KEYS[error.code] ?? "common.upload.failed")) : isApiError(error.cause) ? apiMessage(error.cause) : t("common.upload.failed");
      const unfixable = error.isStorageFailure || error.status === 0 || error.status >= SERVER_FAULT;
      return unfixable && error.code !== STORAGE_ERROR_CODES.aborted ? `${base} ${t("common.upload.reference", { reference: error.reference })}` : base;
    },
    [apiMessage, t],
  );
}
